import { Link } from 'react-router-dom';
import { CheckIcon, MessageCircleIcon } from 'lucide-react';
import { useCartActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { useSite } from '../../contexts/SiteContext';
import { SALON_CODE } from '../../data/codes';
import { ProductImage } from '../../ui/ProductImage';
import { longDate } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import { isSoldOut } from '../../core/rules/stock';
import { telHref } from '../../core/rules/contact';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import type { BookingControl } from './useBooking';

/** Na het versturen: bevestiging, en het thuisprotocol dat bij de behandeling hoort. */
export function BookingDone({
  b
}: {
  b: BookingControl;
}) {
  const {
    policy
  } = useSite();
  const {
    addRoutine,
    applyCode
  } = useCartActions();
  const {
    account,
    name,
    selected,
    session,
    day,
    time,
    upsell,
    upsellTotals,
    close,
    signedIn
  } = b;
  const available = upsellTotals ? upsellTotals.items.filter(p => !isSoldOut(p)) : [];
  const availablePrice = available.reduce((s, p) => s + p.price, 0);
  return <div data-id="element-8602" data-mp="3ihugptgmy7w2">
      <div className="flex flex-col items-center text-center" data-id="element-8603" data-mp="3ihm4m3sc76lx">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blush" aria-hidden="true" data-id="element-8604" data-mp="3dkch4toid50q">
          <CheckIcon className="h-5 w-5 text-ink" strokeWidth={1.5} data-id="element-8605" data-mp="38n2iqoj55j0r" />
        </span>
        <h3 className="mt-6 type-title text-ink" data-id="element-8606" data-mp="33lwlqeig79lf">Tot binnenkort, {account?.customer.firstName ?? name}</h3>
        <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-muted" data-id="element-8607" data-mp="2zlmhp7e7ut97">
          Je aanvraag voor {selected?.name ?? 'je behandeling'}
          {session ? ` (sessie ${session})` : ''} op {day && longDate(day)} om {time}
          {b.confirmedWith ? ` bij ${b.confirmedWith}` : ''} is binnen. {founder.firstName} bevestigt persoonlijk binnen{' '}
          {policy.responseHours} uur.
        </p>
        {/* Na het versturen is de voorwaarde uit de knopbalk weg; hier staat hij nog één keer. */}
        <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-muted" data-id="element-8608" data-mp="1gr9ltwn93eue">Toch niet gelegen? {policyCopy.cancel}.</p>
        {signedIn ? <Link to="/account/afspraken" onClick={close} className="mt-3 text-[14px] text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8609" data-mp="17xeqfgbxl2ry">
            Bekijk het in Mijn omgeving
          </Link> : <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-muted" data-id="element-8610" data-mp="1lpbf1aee4ujl">
            Geen bevestiging gezien? Kijk even in je spambox, of stuur een appje naar{' '}
            <a href={telHref(salon.phone)} className="text-ink underline underline-offset-4" data-id="element-8611" data-mp="1qq8wfkrktkk1">
              {salon.phone}
            </a>
            .
          </p>}
      </div>

      {/* Alles uit het thuisprotocol uitverkocht? Dan niet aanbieden: de knop zou niets toevoegen. */}
      {upsell && upsellTotals && available.length > 0 && <section className="mt-8 border-t border-line pt-6" aria-labelledby="booking-upsell" data-id="element-8612" data-mp="2r6krdgn9y5j">
          <p className="text-[14px] text-clay" data-id="element-8613" data-mp="gqeliasghebu">Het resultaat vasthouden doe je thuis</p>
          <h4 id="booking-upsell" className="mt-2 type-name text-ink" data-id="element-8614" data-mp="bqju864rqmq9">
            {upsell.name}
          </h4>
          <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-8615" data-mp="3k63kqxe21r1m">
            Het thuisprotocol dat {founder.firstName} bij deze behandeling meegeeft, met een huidplan in Mijn omgeving. Na je behandeling werkt ze het persoonlijk bij.
          </p>
          <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-8616" data-mp="3tyraj86mauqu">
            {upsellTotals.items.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-8617" data-mp="31g3wudtyahzs">
                {/* Decoratief: de naam staat ernaast. */}
                <ProductImage src={p.image} alt="" className="h-12 w-10 shrink-0 p-1" data-id="element-8618" data-mp="2wg98agxne4hw" />
                <span className="flex-1 text-[14px] leading-snug text-ink" data-id="element-8619" data-mp="3a4sr6jejadw6">
                  {p.name}
                  {isSoldOut(p) && <span className="block text-[12px] text-clay" data-id="element-8620" data-mp="3f3vzccpt908d">{shopLabels.soldOutShort}</span>}
                </span>
                <span className={`text-[14px] ${isSoldOut(p) ? 'text-muted line-through decoration-line' : 'text-muted'}`} data-id="element-8621" data-mp="3k0w50mir00yy">{euro(p.price)}</span>
              </li>)}
          </ul>
          <button type="button" onClick={() => {
        addRoutine(upsell.id);
        applyCode(SALON_CODE);
        close();
      }} className={`${btnPrimary} mt-5 w-full`} data-id="element-8622" data-mp="3stzbgauz7dk3">
            {/* De prijs van wat er echt in de mand gaat: uitverkochte producten tellen niet mee. */}
            Leg het thuisprotocol klaar · {euro(availablePrice)}
          </button>
          <p className="mt-2 text-center text-[13px] text-muted" data-id="element-8623" data-mp="2ddqtomkmfn0b">Of laat het klaarstaan in de salon en betaal na je behandeling.</p>
        </section>}

      <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} mt-8 w-full`} data-id="element-8624" data-mp="ujfiptotkeqk">
        <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8625" data-mp="pqcs9ufc9lel" />
        Iets doorgeven via WhatsApp
        <span className="sr-only" data-id="element-8626" data-mp="lny6p2kac7jm"> {shopLabels.newTab}</span>
      </a>
    </div>;
}